/* === CSS contract ===
   These rules MUST live in theme-builder.css for this runtime to function.
   The inject pipeline extracts everything between the contract markers and
   appends it to theme-builder.css.

   Pre-animation states are gated behind .builder-anim-ready (added synchronously
   in <head> only when motion is allowed) so that if JS never runs — or the user
   prefers reduced motion — content is fully visible with NO flash.
   ------------------------------------------------------------------
   .builder-anim-ready [data-animation="reveal"],
   .builder-anim-ready [data-animation="text"] .ba-line__inner,
   .builder-anim-ready [data-animation="text"] .ba-word,
   .builder-anim-ready [data-animation="stagger"] > * {
     opacity: 0;
   }
   .builder-anim-ready [data-animation="reveal"],
   .builder-anim-ready [data-animation="stagger"] > * {
     transform: translateY(var(--anim-distance, 28px));
   }
   .builder-anim-ready [data-animation="clip"] {
     clip-path: inset(0 100% 0 0);
   }
   [data-animation="text"] .ba-line { overflow: hidden; }
   [data-animation="marquee"] { overflow: hidden; }
   [data-animation="marquee"] .ba-marquee__track {
     display: inline-flex; flex-wrap: nowrap; white-space: nowrap; will-change: transform;
   }
   [data-ba-magnetic] { will-change: transform; }
   @media (prefers-reduced-motion: reduce) {
     [data-animation],
     [data-animation="stagger"] > *,
     [data-animation="text"] .ba-word,
     [data-animation="text"] .ba-line__inner {
       opacity: 1 !important;
       transform: none !important;
       clip-path: none !important;
       transition: none !important;
       animation: none !important;
     }
   }
   === END CSS contract === */

/* ============================================================
   Builder Animations Runtime — GSAP + ScrollTrigger + Lenis edition.
   Powers data-animation attributes for builder-generated themes.

   Load order in layout/theme.liquid (all defer, document order):
     gsap.min.js → ScrollTrigger.min.js → lenis.min.js → builder-animations.js

   Recipes (attribute: data-animation="...")
     reveal   - fade + slide up on enter
     stagger  - children fade + slide up, staggered
     text     - split heading into lines/words, reveal staggered
     marquee  - seamless infinite horizontal scroll of inner content
     parallax - scroll-scrubbed Y translate (depth)
     clip     - clip-path wipe reveal (good for images)
     counter  - count a number up to its final value on enter
     pin      - pin the element while the page scrolls past it
     scrub    - opacity+Y tied to scroll progress (cinematic)
     magnetic - pointer-follow nudge (hover; pointer devices only)

   Tuning attributes (all optional):
     data-animation-delay      ms
     data-animation-duration   ms
     data-animation-distance   px (reveal/stagger start offset)
     data-animation-stagger    ms between children/words
     data-animation-easing     gsap ease name (default "power3.out")
     data-animation-strength   px (parallax range) | number (magnetic factor)
     data-animation-split      "words" | "lines" (text recipe; default "lines")
     data-animation-direction  "left" | "right" (marquee) | wipe side (clip)
     data-animation-speed      px/s (marquee; default 60)
     data-animation-start      ScrollTrigger start (default "top 85%")

   Graceful degradation: no GSAP → IntersectionObserver handles reveal/stagger/
   text/counter; scroll-only recipes (parallax/scrub/pin) are skipped. Reduced
   motion or Shopify theme-editor design mode → no Lenis, recipes show final state.
   Exports: window.BuilderAnimations = { init, scan }
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var prefersReduced = false;
  try {
    prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* old browser */ }
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  var gsap = window.gsap || null;
  var ScrollTrigger = window.ScrollTrigger || null;
  var Lenis = window.Lenis || null;
  var hasGsap = !!gsap;
  var hasST = !!(gsap && ScrollTrigger);

  // Motion contract baked into <html> by layout-patch (the Animation Director's
  // plan): data-motion intensity, data-motion-smooth (Lenis), data-motion-fx
  // (which heavy recipes are allowed).
  var motion = docEl.getAttribute('data-motion') || 'balanced';
  var smoothAllowed = docEl.getAttribute('data-motion-smooth') !== '0';
  var FX = {};
  (docEl.getAttribute('data-motion-fx') || '').split(/\s+/).forEach(function (f) {
    if (f) FX[f] = true;
  });

  // Bail on reduced-motion OR intensity "off": drop the gate class so CSS
  // pre-hide rules stop applying and every element renders fully visible.
  if (prefersReduced || motion === 'off') {
    docEl.classList.remove('builder-anim-ready');
    window.BuilderAnimations = { init: function () {}, scan: function () {} };
    return;
  }

  // Global timing defaults from the plan's CSS vars (per-element attrs override).
  function cssNum(name, dflt) {
    try {
      var n = parseFloat(getComputedStyle(docEl).getPropertyValue(name));
      return isNaN(n) ? dflt : n;
    } catch (e) { return dflt; }
  }
  var G = {
    duration: cssNum('--anim-duration', 700),
    distance: cssNum('--anim-distance', 28),
    stagger: cssNum('--anim-stagger', 90),
  };

  if (hasST) {
    try { gsap.registerPlugin(ScrollTrigger); } catch (e) { hasST = false; }
  }

  /* ---------------- helpers ---------------- */
  function num(el, attr, dflt) {
    var v = el.getAttribute(attr);
    if (v == null || v === '') return dflt;
    var n = parseFloat(v);
    return isNaN(n) ? dflt : n;
  }
  function str(el, attr, dflt) {
    var v = el.getAttribute(attr);
    return v == null || v === '' ? dflt : v;
  }
  function done(el) {
    if (el.getAttribute('data-ba-done') === '1') return true;
    el.setAttribute('data-ba-done', '1');
    return false;
  }
  function ease(el) { return str(el, 'data-animation-easing', 'power3.out'); }
  function startPos(el) { return str(el, 'data-animation-start', 'top 85%'); }

  /* ---------------- Lenis smooth scroll ---------------- */
  var lenis = null;
  function initLenis() {
    if (!Lenis || designMode || !smoothAllowed) return; // editor / plan: native scroll
    try {
      lenis = new Lenis({ duration: 1.1, smoothWheel: true, smoothTouch: false });
    } catch (e) { lenis = null; return; }
    if (hasST) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      var raf = function (t) { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  }

  /* ---------------- IntersectionObserver fallback (no GSAP) ---------------- */
  var io = null;
  function ensureIO() {
    if (io || !('IntersectionObserver' in window)) return;
    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          entries[i].target.classList.add('is-in');
          io.unobserve(entries[i].target);
        }
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
  }
  function ioReveal(el) {
    if (!('IntersectionObserver' in window)) { el.style.opacity = '1'; el.style.transform = 'none'; return; }
    ensureIO();
    // minimal inline so the CSS .is-in transition (if any) or instant show works
    el.style.transition = 'opacity .7s ease, transform .7s ease';
    io.observe(el);
    el.addEventListener('transitionstart', function () {}, { once: true });
    // is-in toggles visible via inline override
    var obs = new MutationObserver(function () {
      if (el.classList.contains('is-in')) { el.style.opacity = '1'; el.style.transform = 'none'; obs.disconnect(); }
    });
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------------- recipes (GSAP) ---------------- */
  function gReveal(el) {
    gsap.set(el, { opacity: 0, y: num(el, 'data-animation-distance', G.distance) });
    gsap.to(el, {
      opacity: 1, y: 0,
      duration: num(el, 'data-animation-duration', G.duration) / 1000,
      delay: num(el, 'data-animation-delay', 0) / 1000,
      ease: ease(el),
      scrollTrigger: hasST ? { trigger: el, start: startPos(el) } : undefined,
    });
  }

  function gStagger(el) {
    var children = el.children;
    if (!children.length) return gReveal(el);
    gsap.set(children, { opacity: 0, y: num(el, 'data-animation-distance', G.distance) });
    gsap.to(children, {
      opacity: 1, y: 0,
      duration: num(el, 'data-animation-duration', G.duration) / 1000,
      ease: ease(el),
      stagger: num(el, 'data-animation-stagger', G.stagger) / 1000,
      scrollTrigger: hasST ? { trigger: el, start: startPos(el) } : undefined,
    });
  }

  // Split text into lines (wrapped) or words; reveal with a mask slide-up.
  function gText(el) {
    var mode = str(el, 'data-animation-split', 'lines');
    var units = splitText(el, mode);
    if (!units.length) return gReveal(el);
    gsap.set(units, { yPercent: 110, opacity: 0 });
    gsap.to(units, {
      yPercent: 0, opacity: 1,
      duration: num(el, 'data-animation-duration', 800) / 1000,
      ease: ease(el),
      stagger: num(el, 'data-animation-stagger', G.stagger) / 1000,
      scrollTrigger: hasST ? { trigger: el, start: startPos(el) } : undefined,
    });
  }

  // DIY split (free; replaces paid GSAP SplitText). Wraps words; for "lines"
  // each word's inner span is the animated unit and the word sits in an
  // overflow-hidden line wrapper so the mask effect reads as line-by-line.
  function splitText(el, mode) {
    var text = el.textContent || '';
    el.textContent = '';
    var words = text.split(/(\s+)/);
    var units = [];
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (/^\s+$/.test(w)) { el.appendChild(document.createTextNode(w)); continue; }
      if (w === '') continue;
      var line = document.createElement('span');
      line.className = 'ba-line';
      line.style.display = 'inline-block';
      line.style.overflow = 'hidden';
      line.style.verticalAlign = 'top';
      var inner = document.createElement('span');
      inner.className = mode === 'words' ? 'ba-word' : 'ba-line__inner';
      inner.style.display = 'inline-block';
      inner.textContent = w;
      line.appendChild(inner);
      el.appendChild(line);
      units.push(inner);
    }
    return units;
  }

  function gMarquee(el) {
    var inner = el.firstElementChild;
    if (!inner) return;
    // Build a track holding two copies for a seamless loop.
    var track = document.createElement('div');
    track.className = 'ba-marquee__track';
    var clone = inner.cloneNode(true);
    track.appendChild(inner);
    track.appendChild(clone);
    el.appendChild(track);
    var dir = str(el, 'data-animation-direction', 'left') === 'right' ? 1 : -1;
    var speed = num(el, 'data-animation-speed', 60); // px/s
    var width = inner.getBoundingClientRect().width || track.scrollWidth / 2;
    if (!width) return;
    var dur = width / speed;
    gsap.to(track, {
      x: dir * -width, duration: dur, ease: 'none', repeat: -1,
      modifiers: { x: function (x) { return (parseFloat(x) % width) + 'px'; } },
    });
  }

  function gParallax(el) {
    if (!hasST) return;
    var strength = num(el, 'data-animation-strength', 60);
    gsap.fromTo(el, { y: -strength }, {
      y: strength, ease: 'none',
      scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
    });
  }

  function gClip(el) {
    var side = str(el, 'data-animation-direction', 'left');
    var from = side === 'right' ? 'inset(0 0 0 100%)' : side === 'top' ? 'inset(100% 0 0 0)'
      : side === 'bottom' ? 'inset(0 0 100% 0)' : 'inset(0 100% 0 0)';
    gsap.set(el, { clipPath: from });
    gsap.to(el, {
      clipPath: 'inset(0 0 0 0)',
      duration: num(el, 'data-animation-duration', 900) / 1000,
      delay: num(el, 'data-animation-delay', 0) / 1000,
      ease: str(el, 'data-animation-easing', 'power4.inOut'),
      scrollTrigger: hasST ? { trigger: el, start: startPos(el) } : undefined,
    });
  }

  function gCounter(el) {
    var target = parseFloat((el.textContent || '').replace(/[^0-9.\-]/g, ''));
    if (isNaN(target)) return;
    var suffix = (el.textContent || '').replace(/[0-9.,\s\-]/g, '');
    var prefixMatch = (el.textContent || '').match(/^[^0-9]*/);
    var prefix = prefixMatch ? prefixMatch[0].trim() : '';
    var decimals = (String(target).split('.')[1] || '').length;
    var obj = { v: 0 };
    var run = function () {
      gsap.to(obj, {
        v: target, duration: num(el, 'data-animation-duration', 1600) / 1000, ease: 'power2.out',
        onUpdate: function () {
          el.textContent = (prefix ? prefix : '') + obj.v.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (suffix || '');
        },
      });
    };
    if (hasST) ScrollTrigger.create({ trigger: el, start: startPos(el), once: true, onEnter: run });
    else run();
  }

  function gPin(el) {
    if (!hasST) return;
    var dist = num(el, 'data-animation-distance', 50); // vh of extra scroll
    ScrollTrigger.create({
      trigger: el, start: 'top top', end: '+=' + (window.innerHeight * dist / 100),
      pin: true, pinSpacing: true,
    });
  }

  function gScrub(el) {
    if (!hasST) return;
    gsap.fromTo(el, { opacity: 0.25, y: num(el, 'data-animation-distance', 40) }, {
      opacity: 1, y: 0, ease: 'none',
      scrollTrigger: { trigger: el, start: 'top 90%', end: 'top 40%', scrub: true },
    });
  }

  function magnetic(el) {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    var factor = num(el, 'data-animation-strength', 0.3);
    el.setAttribute('data-ba-magnetic', '1');
    el.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      var x = (e.clientX - (r.left + r.width / 2)) * factor;
      var y = (e.clientY - (r.top + r.height / 2)) * factor;
      if (hasGsap) gsap.to(el, { x: x, y: y, duration: 0.4, ease: 'power3.out' });
      else el.style.transform = 'translate(' + x + 'px,' + y + 'px)';
    });
    el.addEventListener('pointerleave', function () {
      if (hasGsap) gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1,0.4)' });
      else el.style.transform = '';
    });
  }

  /* ---------------- dispatch ---------------- */
  var RECIPES = {
    reveal: hasGsap ? gReveal : ioReveal,
    stagger: hasGsap ? gStagger : function (el) { ioReveal(el); },
    text: hasGsap ? gText : ioReveal,
    marquee: hasGsap ? gMarquee : function () {},
    parallax: hasGsap ? gParallax : function () {},
    clip: hasGsap ? gClip : ioReveal,
    counter: hasGsap ? gCounter : function () {},
    pin: hasGsap ? gPin : function () {},
    scrub: hasGsap ? gScrub : ioReveal,
    magnetic: magnetic,
  };

  // Heavy recipes are gated behind the plan's data-motion-fx allowlist. When a
  // section authored one but the plan didn't enable it, degrade gracefully
  // rather than ignoring intent: text/scrub → a plain reveal; parallax/marquee/
  // counter → leave the element in its final, visible state.
  var GATED = { parallax: 1, scrub: 1, marquee: 1, counter: 1, text: 1 };
  function apply(el) {
    if (done(el)) return;
    var type = el.getAttribute('data-animation');
    if (GATED[type] && !FX[type]) {
      if (type === 'text' || type === 'scrub') type = 'reveal';
      else { el.style.opacity = '1'; el.style.transform = 'none'; return; }
    }
    var fn = RECIPES[type] || RECIPES.reveal;
    try { fn(el); } catch (e) { el.style.opacity = '1'; el.style.transform = 'none'; }
  }

  function scan(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-animation]');
    for (var i = 0; i < nodes.length; i++) apply(nodes[i]);
  }

  function killIn(node) {
    if (!hasST || !node) return;
    ScrollTrigger.getAll().forEach(function (st) {
      if (st.trigger && node.contains(st.trigger)) st.kill();
    });
  }

  function init() {
    initLenis();
    scan(document);
    if (hasST) ScrollTrigger.refresh();
    // Shopify theme editor: re-scan added sections, clean up removed ones.
    document.addEventListener('shopify:section:load', function (e) {
      scan(e.target || document);
      if (hasST) ScrollTrigger.refresh();
    });
    document.addEventListener('shopify:section:unload', function (e) {
      killIn(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.BuilderAnimations = { init: init, scan: scan };
})();
